<template>
    <div
        v-for="unit in project.business_units"
        :key="unit"
        class="bu-card-holder"
    >
        <h2 class="bu-title">
            {{ $t("messages.team_member") }} (
            {{
                businessUnitsForSelect.find(
                    (bu) => String(bu.id) === String(unit)
                )?.name || unit
            }}
            )
        </h2>

        <!-- Partners -->

        <div class="row m-0">
            <!--partners-->
            <div class="col-md-6 ps-0">
                <form-el-select
                    name="partners"
                    :options="
                        getAvailableUsersForRole(
                            businessUnitData[unit]?.partners || []
                        )
                    "
                    :multiple="true"
                    :label="$t('fields.senior_partners_partners')"
                    :model="businessUnitData[unit]"
                    :errors="mergedErrors"
                    :error_name="`partners_${unit}`"
                    label-field="fields.partners"
                    :required="true"
                    clearable
                    placeholder="Select the partner leading this project"
                    :action-option-enabled="true"
                    :action-option-label="'Add External Partner'"
                    @action="() => openUserModal('partner')"
                    @change="getData"
                />
            </div>

            <!--associate partners-->
            <div class="col-md-6 px-0">
                <form-el-select
                    name="associate_partners"
                    :options="
                        getAvailableUsersForRole(
                            businessUnitData[unit]?.associate_partners || []
                        )
                    "
                    :multiple="true"
                    :label="$t('fields.associate_partners_SrDirector_director')"
                    :model="businessUnitData[unit]"
                    :errors="mergedErrors"
                    :error_name="`associate_partners_${unit}`"
                    label-field="fields.partners"
                    :required="true"
                    clearable
                    placeholder="choose the senior leader overseeing delivery for this p..."
                    :action-option-enabled="true"
                    :action-option-label="'Add External Associate Partner'"
                    @action="() => openUserModal('associate_partner')"
                    @change="getData"
                />
            </div>
        </div>

        <!-- Managers (inline editable rows) -->
        <div class="row m-0">
            <div class="col-md-12 middle-card managers-row">
                <!-- table header -->
                <div class="row m-0 gx-0 section-header">
                    <div :class="Array.isArray(project.tracks) && project.tracks.length ? 'col-md-6' : 'col-md-11'" class="ps-0">
                        <label class="form-label">{{ $t('fields.manager_SrManager') }} *</label>
                    </div>
                    <div class="col-md-5 ps-0" v-if="Array.isArray(project.tracks) && project.tracks.length">
                        <label class="form-label">{{ $t('fields.lead_tracks') }} *</label>
                    </div>
                    <div class="col-md-1"></div>
                
                </div>
                <div
                    class="row m-1 g-2 align-items-center mt-2"
                    v-for="(row, idx) in (businessUnitData[unit]?.managers?.length || 1)"
                    :key="`mgr-edit-row-${idx}`"
                >
                    <div :class="Array.isArray(project.tracks) && project.tracks.length ? 'col-md-6' : 'col-md-11'" class="ps-0">
                        <form-el-select
                            :name="mgrKey(idx)"
                            :label="null"
                            :model="businessUnitData[unit]"
                            :options="availableManagerOptions(unit, idx)"
                            :required="true"
                            clearable
                            :multiple="false"
                            placeholder="Select the manager(s) responsible for day-to-day delivery"
                            :errors="mergedErrors"
                            :error_name="`${mgrKey(idx)}_${unit}`"
                            :action-option-enabled="true"
                            :action-option-label="'Add External Manager'"
                            @action="() => openUserModal('manager')"
                            @change="syncMgrRowFromModel(unit, idx)"
                        />
                    </div>
                    <div class="col-md-5 ps-0" v-if="Array.isArray(project.tracks) && project.tracks.length">
                        <form-el-select
                            :key="`mgr-track-${unit}-${idx}-${project.tracks?.length || 0}`"
                            :name="mgrTrackKey(idx)"
                            :options="trackOptions"
                            :label="null"
                            :model="businessUnitData[unit]"
                            :error_name="`${mgrTrackKey(idx)}_${unit}`"
                            :required="true"
                            clearable
                            :multiple="false"
                            label-field="name"
                            placeholder="Select the track he/she is leading"
                            @change="syncMgrRowFromModel(unit, idx)"
                        />
                    </div>
                    <div class="col-md-1 ps-0 d-flex align-items-center justify-content-end m-0">
                        <button type="button" class="btn p-0" @click="removeManager(unit, idx)" v-if="idx > 0 && businessUnitData[unit]?.managers?.[idx] != null">
                            <img :src="deleteIcon" alt="delete" />
                        </button>
                    </div>
                </div>
                <!-- <div class=" m-1 col-md-12 px-0 mt-2 note--text">Choose manager and track to lead (if available)</div> -->
            </div>
        </div>

        <!-- Members (inline editable rows) -->
        <div class="row m-0">
            <div class="col-md-12  middle-card managers-row">
                <!-- table header -->
                <div class="row m-0 gx-0 section-header">
                    <div :class="Array.isArray(project.tracks) && project.tracks.length ? 'col-md-6' : 'col-md-11'" class="ps-0">
                        <label class="form-label">Team Member(s) *</label>
                    </div>
                    <div class="col-md-5 ps-0" v-if="Array.isArray(project.tracks) && project.tracks.length">
                        <label class="form-label">Track selection</label>
                    </div>
                    <div class="col-md-1"></div>
                </div>
                <div
                    class="row m-1 g-2 align-items-center mt-2"
                    v-for="(row, idx) in (businessUnitData[unit]?.members?.length || 1)"
                    :key="`mem-edit-row-${idx}`"
                >
                    <div :class="Array.isArray(project.tracks) && project.tracks.length ? 'col-md-6' : 'col-md-11'" class="ps-0">
                        <form-el-select
                            :name="memKey(idx)"
                            :label="null"
                            :model="businessUnitData[unit]"
                            :options="availableMemberOptions(unit, idx)"
                            :required="false"
                            clearable
                            :multiple="false"
                            placeholder="Select all team members contributing to this project"
                            :action-option-enabled="true"
                            :action-option-label="'Add External Member'"
                            @action="() => openUserModal('member')"
                            @change="syncMemRowFromModel(unit, idx)"
                        />
                    </div>
                    <div class="col-md-5 ps-0" v-if="Array.isArray(project.tracks) && project.tracks.length">
                        <form-el-select
                            :key="`mem-track-${unit}-${idx}-${project.tracks?.length || 0}`"
                            :name="memTrackKey(idx)"
                            :options="trackOptions"
                            :label="null"
                            :model="businessUnitData[unit]"
                            :error_name="`${memTrackKey(idx)}_${unit}`"
                            clearable
                            :multiple="false"
                            label-field="name"
                            placeholder="Select the track he/she is part of"
                            @change="syncMemRowFromModel(unit, idx)"
                        />
                    </div>
                    <div class="col-md-1 ps-0 d-flex align-items-center justify-content-end m-0">
                        <button type="button" class="btn p-0" @click="removeMember(unit, idx)" :title="$t ? $t('messages.delete') : 'Delete'" v-if="idx > 0 && businessUnitData[unit]?.members?.[idx] != null">
                            <img :src="deleteIcon" alt="delete" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
    
    <!-- User Creation Modal -->
    <create-user-modal
        v-model="showUserModal"
        :user-type="currentUserType"
        @created="onUserCreated"
    />
</template>

<script setup>
import {
    reactive,
    watch,
    nextTick,
    computed,
    onMounted,
    onBeforeUnmount,
    ref,
} from "vue";
import deleteIcon from "@/assets/img/delete-table.png";
import defaultUserIcon from "@/assets/img/user.png";
import { generateAvatarDataUrl } from "@/utils/avatar";
import CreateUserModal from "./CreateUserModal.vue";

function getAvatar(id) {
    const user = props.teamForSelect.find((opt) => opt.id === id);
    const url = user?.user_avatar?.url;
    if (url && url.length > 0) {
        return url;
    }
    const name =
        user?.name ||
        user?.full_name ||
        user?.fullName ||
        user?.email ||
        "User";
    return generateAvatarDataUrl(name, { size: 64 });
}

const props = defineProps({
    project: {
        type: Object,
        default: () => ({ business_units: [] }),
    },
    formErrors: {
        type: Object,
        default: () => ({}),
    },
    v$: {
        type: Object,
        default: () => ({}),
    },
    businessUnitsForSelect: {
        type: Array,
        default: () => [],
    },
    teamForSelect: {
        type: Array,
        default: () => [],
    },
    defaultPartnersForSelect: {
        type: Array,
        default: () => [],
    },
});

const businessUnitData = reactive({});

// User modal state
const showUserModal = ref(false);
const currentUserType = ref('member');

const mergedErrors = computed(() => {
    const merged = {};
    // Copy incoming errors safely
    if (props.formErrors && typeof props.formErrors === "object") {
        Object.keys(props.formErrors).forEach((key) => {
            const val = props.formErrors[key];
            merged[key] = Array.isArray(val) ? [...val] : [String(val)];
        });
    }
    // Add friendly validation when manager selected but track is missing
    if (
        Array.isArray(props.project?.business_units) &&
        projectHasTracks()
    ) {
        props.project.business_units.forEach((unit) => {
            const u = businessUnitData[unit] || {};
            const managers = Array.isArray(u.managers) ? u.managers : [];
            const tracks =
                Array.isArray(u.managers_tracks) ? u.managers_tracks : [];
            for (let idx = 0; idx < managers.length; idx++) {
                const hasManager =
                    managers[idx] != null && managers[idx] !== "";
                const trackVal = tracks[idx];
                const missingTrack = trackVal == null || trackVal === "" || trackVal === "null";
                // Allow "na" (Not Applicable) as a valid track selection
                const hasValidTrack = trackVal === "na" || (!missingTrack && trackVal !== "");
                if (hasManager && !hasValidTrack) {
                    const key = `${mgrTrackKey(idx)}_${unit}`;
                    merged[key] = ["Manager's tracks are required"];
                }
            }
        });
    }
    return merged;
});

// Computed property for available users to ensure reactivity
const availableUsersForRole = computed(() => {
    return function(currentSelections = []) {
        const assignedUserIds = new Set();

        // Collect all assigned users across all business units and roles
        Object.keys(businessUnitData).forEach((buId) => {
            const unitData = businessUnitData[buId];

            if (Array.isArray(unitData.partners)) {
                unitData.partners.forEach((id) => assignedUserIds.add(id));
            }

            if (Array.isArray(unitData.associate_partners)) {
                unitData.associate_partners.forEach((id) =>
                    assignedUserIds.add(id)
                );
            }

            if (Array.isArray(unitData.managers)) {
                unitData.managers.forEach((id) => assignedUserIds.add(id));
            }

            if (Array.isArray(unitData.members)) {
                unitData.members.forEach((id) => assignedUserIds.add(id));
            }
        });

        // Remove current selections from the exclusion list so they remain visible
        currentSelections.forEach((id) => assignedUserIds.delete(id));

        // Return filtered users from the latest teamForSelect
        return (props.teamForSelect || []).filter((opt) => !assignedUserIds.has(opt.id));
    };
});

// Helper function to get available users for role
function getAvailableUsersForRole(currentSelections = []) {
    return availableUsersForRole.value(currentSelections);
}

const trackOptions = computed(() => {
    const tracks =
        Array.isArray(props.project?.tracks) &&
        props.project.has_multiple_tracks
            ? props.project.tracks
            : [];
    const options = tracks.map((label, index) => ({
        id: String(index),
        name: label || String(index + 1),
    }));
    
    return [
        
        ...options,
        { id: "na", name: "Not Applicable" }, 
    ];
});

// Function to apply default partners for a business unit
function applyDefaultPartners(unit) {
    if (
        !props.defaultPartnersForSelect ||
        !Array.isArray(props.defaultPartnersForSelect)
    ) {
        return;
    }

    // Find default partners for this business unit
    const defaultPartners = props.defaultPartnersForSelect.filter(
        (defaultPartner) =>
            String(defaultPartner.business_unit_id) === String(unit)
    );

    if (defaultPartners.length > 0) {
        // Extract partner IDs
        const defaultPartnerIds = defaultPartners.map((dp) => dp.partner_id);

        // Apply default partners if no partners are already selected
        if (
            !businessUnitData[unit] ||
            !businessUnitData[unit].partners ||
            businessUnitData[unit].partners.length === 0
        ) {
            if (!businessUnitData[unit]) {
                businessUnitData[unit] = {
                    partners: [],
                    associate_partners: [],
                    managers: [],
                    managers_tracks: [],
                    members: [],
                    members_tracks: [],
                };
            }
            businessUnitData[unit].partners = [...defaultPartnerIds];
        }
    }
}

// Transform API members data to component format
function transformApiMembersData() {
    if (!props.project?.members || !props.project?.business_units) return;

    const membersData = props.project.members;
    const businessUnits = props.project.business_units;

    // Initialize business unit data structure
    businessUnits.forEach((unitId) => {
        if (!businessUnitData[unitId]) {
            businessUnitData[unitId] = {
                partners: [],
                associate_partners: [],
                managers: [],
                managers_tracks: [],
                members: [],
                members_tracks: [],
            };
        }
    });

    // Get business unit names for mapping
    const businessUnitNames = {};
    props.businessUnitsForSelect.forEach((bu) => {
        businessUnitNames[bu.name] = bu.id;
        businessUnitNames[String(bu.id)] = bu.id; // Also map ID to ID for safety
    });

    // Helper: If business_unit is empty and project has only one BU, use that
    const getUnitId = (businessUnit) => {
        if (businessUnit) {
            return businessUnitNames[businessUnit];
        }
        // Fallback for old projects: if only one business unit, use it
        if (businessUnits.length === 1) {
            return businessUnits[0];
        }
        return undefined;
    };

    // Transform partners
    if (Array.isArray(membersData.partners)) {
        membersData.partners.forEach((partner) => {
            const unitId = getUnitId(partner.business_unit);
            if (
                unitId &&
                businessUnitData[unitId] &&
                partner.status == "ACTIVE"
            ) {
                if (!businessUnitData[unitId].partners.includes(partner.id)) {
                    businessUnitData[unitId].partners.push(partner.id);
                }
            }
        });
    }

    // Transform associate partners
    if (Array.isArray(membersData.associate_partners)) {
        membersData.associate_partners.forEach((partner) => {
            const unitId = getUnitId(partner.business_unit);
            if (
                unitId &&
                businessUnitData[unitId] &&
                partner.status == "ACTIVE"
            ) {
                if (
                    !businessUnitData[unitId].associate_partners.includes(
                        partner.id
                    )
                ) {
                    businessUnitData[unitId].associate_partners.push(
                        partner.id
                    );
                }
            }
        });
    }

    // Transform members with track information
    if (Array.isArray(membersData.members)) {
        membersData.members.forEach((member) => {
            const unitId = getUnitId(member.business_unit);
            if (
                unitId &&
                businessUnitData[unitId] &&
                member.status == "ACTIVE"
            ) {
                if (!businessUnitData[unitId].members.includes(member.id)) {
                    businessUnitData[unitId].members.push(member.id);

                    // Map track name to track index (handles renamed tracks by falling back to track_id)
                    let trackIndex = null;
                    if (member.designated_track && props.project?.tracks && Array.isArray(props.project.tracks)) {
                        const designatedTrackName = String(member.designated_track).trim();
                        
                        // First try to match by track name/title
                        trackIndex = props.project.tracks.findIndex((track) => {
                            const trackName = typeof track === "string" ? track : (track?.name || "");
                            return String(trackName).trim() === designatedTrackName;
                        });
                        
                        // If not found by name and we have track_id, try matching by track ID (for renamed tracks)
                        if (trackIndex === -1 && member.track_id && props.project?.track_ids && Array.isArray(props.project.track_ids)) {
                            trackIndex = props.project.track_ids.findIndex((id) => id === member.track_id);
                        }
                        
                        if (trackIndex === -1) trackIndex = null; // Track not found
                    }
                    // Store track index as string (not JSON stringified for numeric indices)
                    businessUnitData[unitId].members_tracks.push(trackIndex === null ? null : String(trackIndex));
                }
            }
        });
    }

    // Transform team leads (treat as managers) with track information
    if (Array.isArray(membersData.team_lead)) {
        membersData.team_lead.forEach((lead) => {
            const unitId = getUnitId(lead.business_unit);
            if (
                unitId && businessUnitData[unitId] && lead.status
                    ? lead.status == "ACTIVE"
                    : ""
            ) {
                if (!businessUnitData[unitId].managers.includes(lead.id)) {
                    businessUnitData[unitId].managers.push(lead.id);

                    // Map track name to track index (handles renamed tracks by falling back to track_id)
                    let trackIndex = null;
                    if (lead.designated_track && props.project?.tracks && Array.isArray(props.project.tracks)) {
                        const designatedTrackName = String(lead.designated_track).trim();
                        
                        // First try to match by track name/title
                        trackIndex = props.project.tracks.findIndex((track) => {
                            const trackName = typeof track === "string" ? track : (track?.name || "");
                            return String(trackName).trim() === designatedTrackName;
                        });
                        
                        // If not found by name and we have track_id, try matching by track ID (for renamed tracks)
                        if (trackIndex === -1 && lead.track_id && props.project?.track_ids && Array.isArray(props.project.track_ids)) {
                            trackIndex = props.project.track_ids.findIndex((id) => id === lead.track_id);
                        }
                        
                        if (trackIndex === -1) trackIndex = null; // Track not found
                    }
                    // Only default to "na" if no track is specified in database AND tracks are available
                    // Don't set "na" if trackIndex was found (trackIndex >= 0)
                    if (trackIndex === null && projectHasTracks()) {
                        trackIndex = "na";
                    }
                    // Store track index as string (not JSON stringified for numeric indices)
                    businessUnitData[unitId].managers_tracks.push(trackIndex === null ? null : (trackIndex === "na" ? "na" : String(trackIndex)));
                }
            }
        });
    }
}

// initialize immediately so template has data on first render
function hydrateFromProject() {
    if (!(props.project && props.project.business_units)) return;

    // First, try to transform API members data if it exists
    if (props.project.members) {
        transformApiMembersData();
    }

    // Then check for saved business_units_data
    const saved = props.project.business_units_data || {};
    props.project.business_units.forEach((unit) => {
        const unitSaved = saved[unit];
        if (unitSaved) {
            // Handle both old and new data structures
            let managers = [];
            let managers_tracks = [];
            let members = [];
            let members_tracks = [];

            // Check if managers is in new format (array of objects with user_id and track_id)
            if (
                Array.isArray(unitSaved.managers) &&
                unitSaved.managers.length > 0 &&
                unitSaved.managers[0] !== null &&
                typeof unitSaved.managers[0] === "object" &&
                "user_id" in unitSaved.managers[0]
            ) {
                console.log("New format: managers", unitSaved.managers);
                // New format: extract user IDs and track IDs
                managers = unitSaved.managers.map((m) => m.user_id);
                managers_tracks = unitSaved.managers.map((m) => m.track_id);
            } else {
                // Old format: separate arrays
                managers = Array.isArray(unitSaved.managers)
                    ? unitSaved.managers.slice()
                    : [];
                managers_tracks = Array.isArray(unitSaved.managers_tracks)
                    ? unitSaved.managers_tracks.slice()
                    : [];
            }

            // Check if members is in new format
            if (
                Array.isArray(unitSaved.members) &&
                unitSaved.members.length > 0 &&
                unitSaved.members[0] !== null &&
                typeof unitSaved.members[0] === "object" &&
                "user_id" in unitSaved.members[0]
            ) {
                // New format: extract user IDs and track IDs
                members = unitSaved.members.map((m) => m.user_id);
                members_tracks = unitSaved.members.map((m) => m.track_id);
            } else {
                // Old format: separate arrays
                members = Array.isArray(unitSaved.members)
                    ? unitSaved.members.slice()
                    : [];
                members_tracks = Array.isArray(unitSaved.members_tracks)
                    ? unitSaved.members_tracks.slice()
                    : [];
            }

            businessUnitData[unit] = {
                partners: Array.isArray(unitSaved.partners)
                    ? unitSaved.partners.slice()
                    : [],
                associate_partners: Array.isArray(unitSaved.associate_partners)
                    ? unitSaved.associate_partners.slice()
                    : [],
                managers,
                managers_tracks,
                members,
                members_tracks,
            };
        } else if (!businessUnitData[unit]) {
            businessUnitData[unit] = {
                partners: [],
                associate_partners: [],
                managers: [],
                managers_tracks: [],
                members: [],
                members_tracks: [],
            };
        }

        // Apply default partners for this unit
        applyDefaultPartners(unit);

        // Ensure we always have a trailing blank row for managers/members
        ensureBlankRows(unit);
        ensureTempBindings(unit);
    });
}

async function getData() {
    await nextTick();

    // Update project model with business units data
    if (props.project) {
        // Create a deep copy to avoid reference issues
        const dataToSave = {};
        Object.keys(businessUnitData).forEach((unitKey) => {
            const unitState = businessUnitData[unitKey];
            if (unitState) {
                // Build filtered indices skipping empty placeholder rows
                const mgrPairs = Array.isArray(unitState.managers)
                    ? unitState.managers
                          .map((userId, idx) => ({ userId, track: unitState.managers_tracks?.[idx] }))
                          .filter((p) => p.userId != null && p.userId !== "")
                    : [];
                const memPairs = Array.isArray(unitState.members)
                    ? unitState.members
                          .map((userId, idx) => ({ userId, track: unitState.members_tracks?.[idx] }))
                          .filter((p) => p.userId != null && p.userId !== "")
                    : [];
                dataToSave[unitKey] = {
                    partners: Array.isArray(unitState.partners)
                        ? [...unitState.partners]
                        : [],
                    associate_partners: Array.isArray(
                        unitState.associate_partners
                    )
                        ? [...unitState.associate_partners]
                        : [],
                    // Convert managers and tracks to combined structure (skip blank rows)
                    managers: mgrPairs.map((p) => ({ user_id: p.userId, track_id: p.track })),
                    // Convert members and tracks to combined structure (skip blank rows)
                    members: memPairs.map((p) => ({ user_id: p.userId, track_id: p.track })),
                };
            }
        });
        props.project.business_units_data = dataToSave;
    }
}

function addManager(unit) {
    const u = businessUnitData[unit];
    if (!u) return;
    const managerId = u.new_manager_id;
    const track = u.new_manager_track ?? null;
    if (managerId == null) return;

    // Note: Managers can be added without tracks even if tracks are available

    if (!Array.isArray(u.managers)) u.managers = [];
    if (!Array.isArray(u.managers_tracks)) u.managers_tracks = [];
    u.managers.push(Number(managerId));
    u.managers_tracks.push(track);
    u.new_manager_id = null;
    u.new_manager_track = null;
    ensureBlankRows(unit);
    ensureTempBindings(unit);
    getData();
}

function removeManager(unit, idx) {
    const u = businessUnitData[unit];
    if (!u || !Array.isArray(u.managers) || !Array.isArray(u.managers_tracks))
        return;

        console.log("removeManager", idx);
        console.log("u.managers", u.managers);
        console.log("u.managers_tracks", u.managers_tracks);
    u.managers.splice(idx, 1);
    u.managers_tracks.splice(idx, 1);
    console.log("u.managers", u.managers);
    console.log("u.managers_tracks", u.managers_tracks);
    ensureBlankRows(unit);
    ensureTempBindings(unit);
    getData();
}

function addMember(unit) {
    const u = businessUnitData[unit];
    if (!u) return;
    const memberId = u.new_member_id;
    const track = u.new_member_track ?? null;
    if (memberId == null) return;

    // Note: Members can be added without tracks even if tracks are available

    if (!Array.isArray(u.members)) u.members = [];
    if (!Array.isArray(u.members_tracks)) u.members_tracks = [];
    u.members.push(Number(memberId));
    u.members_tracks.push(track);
    u.new_member_id = null;
    u.new_member_track = null;
    ensureBlankRows(unit);
    ensureTempBindings(unit);
    getData();
}

function removeMember(unit, idx) {
    const u = businessUnitData[unit];
    if (!u || !Array.isArray(u.members) || !Array.isArray(u.members_tracks))
        return;
    u.members.splice(idx, 1);
    u.members_tracks.splice(idx, 1);
    ensureBlankRows(unit);
    ensureTempBindings(unit);
    getData();
}

// Auto-add helpers for the add-row selects
function maybeAddManager(unit) {
    const u = businessUnitData[unit];
    if (!u) return;
    const hasTracks = Array.isArray(props.project?.tracks) && props.project.tracks?.length;
    const m = u.new_manager_id;
    const t = u.new_manager_track;
    if ((hasTracks && m != null && t != null && t !== "") || (!hasTracks && m != null)) {
        addManager(unit);
    }
}

function maybeAddMember(unit) {
    const u = businessUnitData[unit];
    if (!u) return;
    if (u.new_member_id != null) {
        addMember(unit);
    }
}


// keep local state in sync if `project.business_units` changes later
watch(
    () => props.project?.business_units || [],
    (units) => {
        hydrateFromProject();

        // Update business_units_data on project model
        if (props.project) {
            getData();
        }
    },
    { immediate: true, deep: false }
);

// Watch for changes in project members data (for edit mode)
watch(
    () => props.project?.members,
    (newMembers) => {
        if (newMembers && props.project?.business_units) {
            // Clear existing data and re-transform
            Object.keys(businessUnitData).forEach((key) => {
                delete businessUnitData[key];
            });
            hydrateFromProject();
            getData();
        }
    },
    { deep: true }
);

// Watch for changes in defaultPartnersForSelect and apply defaults
watch(
    () => props.defaultPartnersForSelect,
    (newDefaultPartners) => {
        if (
            newDefaultPartners &&
            Array.isArray(newDefaultPartners) &&
            props.project?.business_units
        ) {
            props.project.business_units.forEach((unit) => {
                applyDefaultPartners(unit);
            });
            getData();
        }
    },
    { immediate: true, deep: true }
);

// Watch for changes in businessUnitData and sync to project
watch(
    businessUnitData,
    () => {
        getData();
    },
    { deep: true }
);

// Watch for changes in teamForSelect to refresh all dropdowns
watch(
    () => props.teamForSelect,
    () => {
        // Force reactivity update for all user selection dropdowns
        nextTick(() => {
            // This will trigger re-evaluation of getAvailableUsersForRole
            Object.keys(businessUnitData).forEach(unit => {
                ensureTempBindings(unit);
            });
        });
    },
    { deep: true }
);

// Watch for changes in tracks configuration
watch(
    () => [props.project?.has_multiple_tracks, props.project?.tracks],
    ([newHasTracks, newTracks], [oldHasTracks, oldTracks]) => {
        // Only clear track selections when tracks array actually changes (not just the toggle)
        const oldTracksArray = Array.isArray(oldTracks) ? oldTracks : [];
        const newTracksArray = Array.isArray(newTracks) ? newTracks : [];
        const tracksChanged = JSON.stringify(oldTracksArray) !== JSON.stringify(newTracksArray);
        // Clear tracks when disabled (going from true to false)
        const tracksDisabled = oldHasTracks === true && newHasTracks === false;
        
        // Only clear tracks if tracks array actually changed OR tracks were disabled
        // Don't clear when just toggling has_multiple_tracks from false to true (if tracks haven't changed)
        if (tracksChanged || tracksDisabled) {
            nextTick(() => {
                Object.keys(businessUnitData).forEach((unitKey) => {
                    const unitData = businessUnitData[unitKey];
                    if (unitData) {
                        // Clear only track selections (keep managers/members users intact)
                        if (Array.isArray(unitData.managers_tracks)) {
                            for (let i = 0; i < unitData.managers_tracks.length; i++) {
                                unitData.managers_tracks[i] = undefined;
                                const trackKey = mgrTrackKey(i);
                                if (unitData[trackKey] !== undefined) {
                                    delete unitData[trackKey];
                                }
                            }
                        }
                        if (Array.isArray(unitData.members_tracks)) {
                            for (let i = 0; i < unitData.members_tracks.length; i++) {
                                unitData.members_tracks[i] = undefined;
                                const trackKey = memTrackKey(i);
                                if (unitData[trackKey] !== undefined) {
                                    delete unitData[trackKey];
                                }
                            }
                        }
                        ensureTempBindings(unitKey);
                    }
                });
                getData();
            });
        }
    },
    { deep: true }
);

// persist before unmounting when navigating away
onBeforeUnmount(() => {
    if (props.project) {
        props.project.business_units_data = { ...businessUnitData };
    }
});

onMounted(() => {
    hydrateFromProject();
});

// Helpers for dynamic rows
function projectHasTracks() {
    return Array.isArray(props.project?.tracks) && props.project.tracks?.length;
}

function ensureBlankRows(unit) {
    const u = businessUnitData[unit];
    if (!u) return;
    // Managers
    if (!Array.isArray(u.managers)) u.managers = [];
    if (!Array.isArray(u.managers_tracks)) u.managers_tracks = [];
    const lastMgrIdx = u.managers.length - 1;
    if (lastMgrIdx < 0 || u.managers[lastMgrIdx] != null) {
        u.managers.push(null);
        u.managers_tracks.push(null);
    }
    // Members
    if (!Array.isArray(u.members)) u.members = [];
    if (!Array.isArray(u.members_tracks)) u.members_tracks = [];
    const lastMemIdx = u.members.length - 1;
    if (lastMemIdx < 0 || u.members[lastMemIdx] != null) {
        u.members.push(null);
        u.members_tracks.push(null);
    }
}

function onManagerRowChange(unit, idx) {
    const u = businessUnitData[unit];
    if (!u) return;
    const isLast = idx === u.managers.length - 1;
    const hasBoth = projectHasTracks()
        ? u.managers[idx] != null && u.managers_tracks[idx] != null && u.managers_tracks[idx] !== ""
        : u.managers[idx] != null;
    if (isLast && hasBoth) {
        u.managers.push(null);
        u.managers_tracks.push(null);
    }
    ensureTempBindings(unit);
    getData();
}

function onMemberRowChange(unit, idx) {
    const u = businessUnitData[unit];
    if (!u) return;
    const isLast = idx === u.members.length - 1;
    // Track optional; add row once member (first col) is selected
    if (isLast && u.members[idx] != null) {
        u.members.push(null);
        u.members_tracks.push(null);
    }
    ensureTempBindings(unit);
    getData();
}

// Row-specific available options (allow current selection)
function availableManagerOptions(unit, idx) {
    const u = businessUnitData[unit];
    const current = u?.managers?.[idx];
    return getAvailableUsersForRole(current != null ? [current] : []);
}

function availableMemberOptions(unit, idx) {
    const u = businessUnitData[unit];
    const current = u?.members?.[idx];
    return getAvailableUsersForRole(current != null ? [current] : []);
}

// form-el-select dynamic names for inline rows
function mgrKey(idx) {
    return `mgr_${idx}`;
}
function mgrTrackKey(idx) {
    return `mgr_track_${idx}`;
}
function memKey(idx) {
    return `mem_${idx}`;
}
function memTrackKey(idx) {
    return `mem_track_${idx}`;
}

function ensureTempBindings(unit) {
    const u = businessUnitData[unit];
    if (!u) return;
    
    const maxTrackIndex = Array.isArray(props.project?.tracks) && props.project.has_multiple_tracks 
        ? props.project.tracks.length - 1 
        : -1;
    
    // managers
    if (!Array.isArray(u.managers)) u.managers = [];
    if (!Array.isArray(u.managers_tracks)) u.managers_tracks = [];
    for (let i = 0; i < u.managers.length; i++) {
        u[mgrKey(i)] = u.managers[i] ?? null;
        let trackIdx = u.managers_tracks[i];
        
        // Parse JSON stringified values for backward compatibility
        if (typeof trackIdx === "string" && trackIdx.startsWith('"') && trackIdx.endsWith('"')) {
            try {
                trackIdx = JSON.parse(trackIdx);
            } catch (e) {
                // If parsing fails, keep original value
            }
        }
        
        // Allow "na" (Not Applicable) option
        if (trackIdx === "na") {
            u[mgrTrackKey(i)] = "na";
        } else if (trackIdx != null && trackIdx !== "" && trackIdx !== "null") {
            const idx = parseInt(trackIdx, 10);
            // Validate track index - clear if invalid
            if (isNaN(idx) || idx < 0 || idx > maxTrackIndex) {
                u[mgrTrackKey(i)] = null;
                u.managers_tracks[i] = null;
            } else {
                // Store as string for form binding
                u[mgrTrackKey(i)] = String(idx);
            }
        } else {
            // If manager exists but no track selected and tracks are available, default to "na"
            if (u.managers[i] != null && projectHasTracks()) {
            console.log('defaulting to na', u[mgrTrackKey(i)], u.managers_tracks[i]);
                u[mgrTrackKey(i)] = null;
                u.managers_tracks[i] = null;
            } else {
                u[mgrTrackKey(i)] = null;
            }
        }
    }
    // members
    if (!Array.isArray(u.members)) u.members = [];
    if (!Array.isArray(u.members_tracks)) u.members_tracks = [];
    for (let i = 0; i < u.members.length; i++) {
        u[memKey(i)] = u.members[i] ?? null;
        let trackIdx = u.members_tracks[i];
        
        // Parse JSON stringified values for backward compatibility
        if (typeof trackIdx === "string" && trackIdx.startsWith('"') && trackIdx.endsWith('"')) {
            try {
                trackIdx = JSON.parse(trackIdx);
            } catch (e) {
                // If parsing fails, keep original value
            }
        }
        
        // Allow "na" (Not Applicable) option
        if (trackIdx === "na") {
            u[memTrackKey(i)] = "na";
        } else if (trackIdx != null && trackIdx !== "" && trackIdx !== "null") {
            const idx = parseInt(trackIdx, 10);
            // Validate track index - clear if invalid
            if (isNaN(idx) || idx < 0 || idx > maxTrackIndex) {
                u[memTrackKey(i)] = null;
                u.members_tracks[i] = null;
            } else {
                // Store as string for form binding
                u[memTrackKey(i)] = String(idx);
            }
        } else {
            u[memTrackKey(i)] = null;
        }
    }
}

function syncMgrRowFromModel(unit, idx) {
    const u = businessUnitData[unit];
    if (!u) return;
    let val = u[mgrKey(idx)];
    u.managers[idx] = val == null || val === "" ? null : Number(val);
    let trackVal = u[mgrTrackKey(idx)] ?? null;
    // If manager is selected but no track is selected and tracks are available, default to "na"
    // if (u.managers[idx] != null && (trackVal == null || trackVal === "") && projectHasTracks()) {
    //     trackVal = "na";
    //     u[mgrTrackKey(idx)] = "na";
    // }
    u.managers_tracks[idx] = trackVal;
    console.log('trackVal', trackVal);
    console.log('u.managers_tracks', u.managers_tracks);
    onManagerRowChange(unit, idx);
}

function syncMemRowFromModel(unit, idx) {
    const u = businessUnitData[unit];
    if (!u) return;
    let val = u[memKey(idx)];
    u.members[idx] = val == null || val === "" ? null : Number(val);
    u.members_tracks[idx] = u[memTrackKey(idx)] ?? null;
    onMemberRowChange(unit, idx);
}

// User modal functions
function openUserModal(userType) {
    currentUserType.value = userType;
    showUserModal.value = true;
}

function onUserCreated(createdUser) {
    if (createdUser && createdUser.id) {
        // Add new user to teamForSelect if not present
        const exists = props.teamForSelect.find((u) => u.id === createdUser.id);
        if (!exists) {
            props.teamForSelect.push({
                id: createdUser.id,
                name: createdUser.name,
                full_name: createdUser.name,
                email: createdUser.email,
                label: createdUser.name,
                // Add other necessary fields for user display
                user_avatar: null,
                designation: createdUser.designation,
                company: createdUser.company,
                nationality: createdUser.nationality,
                contractor: createdUser.contractor,
            });
        }
        
        // Force reactivity update to make new user appear in all dropdowns
        props.teamForSelect = [...props.teamForSelect];
        
        // Force refresh all dropdown options
        refreshAllUserDropdowns();
        
        // Auto-select the new user in the appropriate role field
        selectUserInRole(createdUser.id, currentUserType.value);
    }
}

function refreshAllUserDropdowns() {
    // Force reactivity update for all user selection dropdowns
    nextTick(() => {
        // This will trigger re-evaluation of getAvailableUsersForRole for all dropdowns
        Object.keys(businessUnitData).forEach(unit => {
            ensureTempBindings(unit);
        });
        
        // Trigger a re-render of all form-el-select components
        const event = new Event('user-options-updated');
        window.dispatchEvent(event);
    });
}

function selectUserInRole(userId, userType) {
    // Find the first business unit to add the user to
    const firstUnit = props.project?.business_units?.[0];
    if (!firstUnit) return;
    
    const unitData = businessUnitData[firstUnit];
    if (!unitData) return;
    
    switch (userType) {
        case 'partner':
            if (!Array.isArray(unitData.partners)) unitData.partners = [];
            if (!unitData.partners.includes(userId)) {
                unitData.partners.push(userId);
            }
            break;
        case 'associate_partner':
            if (!Array.isArray(unitData.associate_partners)) unitData.associate_partners = [];
            if (!unitData.associate_partners.includes(userId)) {
                unitData.associate_partners.push(userId);
            }
            break;
        case 'manager':
            // Add to first empty manager slot or create new one
            if (!Array.isArray(unitData.managers)) unitData.managers = [];
            const emptyMgrIndex = unitData.managers.findIndex(m => m == null);
            if (emptyMgrIndex >= 0) {
                unitData.managers[emptyMgrIndex] = userId;
            } else {
                unitData.managers.push(userId);
                unitData.managers_tracks.push(null);
            }
            ensureBlankRows(firstUnit);
            ensureTempBindings(firstUnit);
            break;
        case 'member':
            // Add to first empty member slot or create new one
            if (!Array.isArray(unitData.members)) unitData.members = [];
            const emptyMemIndex = unitData.members.findIndex(m => m == null);
            if (emptyMemIndex >= 0) {
                unitData.members[emptyMemIndex] = userId;
            } else {
                unitData.members.push(userId);
                unitData.members_tracks.push(null);
            }
            ensureBlankRows(firstUnit);
            ensureTempBindings(firstUnit);
            break;
    }
    
    // Update the project data
    getData();
}
</script>
<style scoped>
.custom-flex-row {
    display: flex;
    flex-wrap: wrap;
}
.custom-flex-col-end {
    width: 3%;
}
.custom-flex-col-right {
    width: 44%;
}
.custom-flex-col-left {
    width: 50%;
}
/* managers/members section header styling */
.section-header {
    background: #DFDCE5;
    border-bottom: 1px solid #D7D4DE;
    padding: 8px 12px;
    margin: 0;
}
/* make header band span full card width */
.middle-card.managers-row {
    padding-left: 0 !important;
    padding-right: 0 !important;
    padding-top: 0 !important;
    overflow: hidden;
}
.members-row {
    padding-left: 0 !important;
    padding-right: 0 !important;
    padding-top: 0 !important;
    overflow: hidden;
    margin-top: 0 !important;
}
.middle-card.managers-row,
.members-row {
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
}
.section-header {
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
    background-clip: padding-box;
}
</style>
